import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Descriptions, Divider, Empty, Space, Spin, Tabs, Tag } from "antd";
import AuthRepo from "@/core/repos/auth.repo";
import container from "@/lib/di/container";
import { useConfigurationPermissions } from "@/core/hooks/useConfigurationPermissions";
import type { FactoryIntegration, FactoryIntegrationCatalog } from "@/core/models/factory-integration-settings.model";
import { factoryIntegrationSettingsRepo, FactorySettingsError } from "@/core/repos/factory-integration-settings.repo";
import { factoryRuntimeSettings, useFactoryRuntimeSettings } from "@/core/stores/factory-runtime-settings.store";
import { MatBaoConnectionSettingsModal } from "../../customer/electronic-contract/modals/MatBaoConnectionSettingsModal";
import IntegrationSettingsForm from "./IntegrationSettingsForm";

const authRepo = container.get(AuthRepo);
const groups = [{ id: "general", label: "Nhà máy", ids: ["general"] }, { id: "payment", label: "Thanh toán", ids: ["payment-bank", "payment-vnpay"] },
  { id: "invoice", label: "Hóa đơn điện tử", ids: ["invoice"] }, { id: "accounting", label: "Kế toán", ids: ["accounting"] },
  { id: "econtract", label: "Hợp đồng điện tử", ids: [] }, { id: "ai", label: "Trợ lý AI", ids: ["ai-deepseek", "ai-cerebras"] }];

export default function IntegrationSettingsPage() {
  const user = authRepo.useDetails();
  const permissions=useConfigurationPermissions();
  return permissions.access.integrations.view ? <AdminIntegrationSettings key={permissions.sessionKey} factoryId={user!.waterFactoryId} permissions={permissions}/>
    : <Alert type="warning" showIcon message="Bạn không có quyền xem cấu hình nhà máy và tích hợp." />;
}

function AdminIntegrationSettings({ factoryId,permissions }: { factoryId: string;permissions:ReturnType<typeof useConfigurationPermissions> }) {
  const [catalog, setCatalog] = useState<FactoryIntegrationCatalog | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reload, setReload] = useState(0);
  const request = useRef(0);
  const load = useCallback(async () => {
    const permitted=permissions.guard('integrations','view');if(!permitted())return;
    const version = ++request.current;
    setLoading(true); setCatalog(null); setError(""); setNotice(""); setReload(value => value + 1);
    try {
      const response = await factoryIntegrationSettingsRepo.getCatalog();
      if (version !== request.current||!permitted()) return;
      if (response.factoryId !== factoryId) { setError("Nhà máy trong phiên đăng nhập đã thay đổi. Vui lòng đăng nhập lại."); return; }
      setCatalog(response);
    } catch (failure) { if (version === request.current) setError(failure instanceof FactorySettingsError ? failure.message : "Không tải được cấu hình nhà máy."); }
    finally { if (version === request.current) setLoading(false); }
  }, [factoryId,permissions.guard]);
  useEffect(() => { void load(); return () => { ++request.current; }; }, [load]);
  const saved = (item: FactoryIntegration) => {
    setNotice(`Đã lưu cấu hình ${item.title.toLowerCase()}.`);
    setCatalog(previous => previous ? { ...previous, integrations: previous.integrations.map(current => current.id === item.id ? item : current) } : previous);
    if (item.id === "general" || item.id.startsWith("ai-")) void factoryRuntimeSettings.refresh();
  };
  return <div className="p-4">
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Alert type="info" showIcon message="Cấu hình áp dụng cho nhà máy trong phiên đăng nhập. UAT và production lưu riêng. Thông tin xác thực đã lưu không được hiển thị lại." />
      <Button onClick={() => void load()} loading={loading}>Tải lại cấu hình</Button>
      {error && <Alert type="error" showIcon message={error} />}
      {notice && <Alert type="success" showIcon message={notice} />}
      <Spin spinning={loading}>
        {catalog && <>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="Nhà máy">{catalog.factoryName || catalog.factoryId}</Descriptions.Item>
            <Descriptions.Item label="Môi trường"><Tag color={catalog.environment === "production" ? "green" : "orange"}>{catalog.environment === "production" ? "Production" : "UAT"}</Tag></Descriptions.Item>
          </Descriptions>
          {!catalog.canEdit && <Alert className="mt-3" type="warning" showIcon message="Máy chủ hiện chỉ cho phép xem cấu hình." />}
          <Tabs className="mt-3" destroyInactiveTabPane items={groups.filter(group=>group.id!=="econtract"||permissions.access.contracts.view).map(group => ({ key: group.id, label: group.label,
            children: group.id === "econtract" ? <MatBaoConnectionSettingsModal isModalOpen inline /> : <>
              {catalog.integrations.filter(item => group.ids.includes(item.id)).map((item, index) => <div key={`${item.id}:${item.revision}:${reload}`}>
                {index > 0 && <Divider />}
                <IntegrationSettingsForm integration={item} writable={catalog.canEdit&&permissions.access.integrations.edit} guard={()=>permissions.guard('integrations','edit')} onSaved={saved} />
              </div>)}
              {!catalog.integrations.some(item => group.ids.includes(item.id)) && <Empty description="Máy chủ chưa hỗ trợ nhóm cấu hình này." />}
            </>,
          }))} />
        </>}
      </Spin>
    </Space>
  </div>;
}
